import { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";
import { formatDateTime, nowLocalSql } from "../utils/datetime";
import { getKategoriFromKode } from "../utils/productCode";
import { includesSearch } from "../utils/search";
import { Boxes, ClipboardList, ImageOff, PackagePlus, Save, Search, ShieldAlert, CheckCircle2, Clock } from "lucide-react";
import RequestDeleteModal from "../modals/RequestDeleteModal";
import RequestEditModal from "../modals/RequestEditModal";

function ProductIcon({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }

  return <img src={src} alt={alt} className="h-10 w-10 rounded-md border border-zinc-200 object-cover" loading="lazy" onError={() => setBroken(true)} />;
}

function DeleteReasonContent({ item, reasonRef }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (reasonRef) reasonRef.current = reason;
  }, [reason, reasonRef]);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-[13px] text-zinc-700">
        <div className="font-semibold text-zinc-900 mb-2">Konfirmasi hapus transaksi</div>
        <div className="grid grid-cols-[60px_1fr] gap-1">
          <div className="text-zinc-500">Produk</div>
          <div className="font-medium text-zinc-900">{item?.nama_barang || item?.kode_barang}</div>
          <div className="text-zinc-500">Qty</div>
          <div className="font-medium text-zinc-900">{item?.qty ?? "-"}</div>
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Alasan penghapusan</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm min-h-[100px]"
          placeholder="Contoh: salah input qty, barang batal, dll."
        />
        <div className="mt-1.5 text-[11px] text-zinc-500">Wajib diisi. Tercatat dalam log.</div>
      </div>
    </div>
  );
}

function ApprovalBadge({ status }) {
  const value = String(status || "").trim().toUpperCase();
  if (value === "PENDING") return <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"><Clock className="h-3 w-3" /> Menunggu</span>;
  if (value === "APPROVED") return <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20"><CheckCircle2 className="h-3 w-3" /> Disetujui</span>;
  if (value === "REJECTED") return <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20"><ShieldAlert className="h-3 w-3" /> Ditolak</span>;
  return <span className="inline-flex items-center rounded-md bg-zinc-50 px-2 py-1 text-[11px] font-medium text-zinc-600 ring-1 ring-inset ring-zinc-500/20">-</span>;
}

function KpiCard({ title, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group hover:border-zinc-300 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-medium text-zinc-500 group-hover:text-zinc-700 transition-colors">{title}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" strokeWidth={2} />}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</div>
    </div>
  );
}

function StockIn() {
  const { isAdmin } = useAuth();
  const dialog = useDialog();
  const [products, setProducts] = useState([]);
  const [stockIn, setStockIn] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productOpen, setProductOpen] = useState(false);
  const blurTimerRef = useRef(null);
  const [form, setForm] = useState({ kode_barang: "", qty: 1, keterangan: "" });
  const [allocation, setAllocation] = useState({ shopee: "", tokopedia: "", spare: "" });

  const fetchProducts = async () => {
    const res = await api.get("/products");
    setProducts(res.data || []);
  };

  const fetchStockIn = async () => {
    const res = await api.get("/stock-in?limit=100");
    setStockIn(res.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchProducts(), fetchStockIn()]);
        setError("");
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Gagal memuat data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedProduct = useMemo(() => products.find((p) => p.kode_barang === form.kode_barang) || null, [products, form.kode_barang]);
  const selectedKategori = useMemo(() => getKategoriFromKode(form.kode_barang), [form.kode_barang]);
  const needsAllocation = selectedKategori === "PRODUK UTAMA";
  const qtyValue = Math.trunc(Number(form.qty)) || 0;

  const allocationNumbers = useMemo(() => {
    const toQty = (value) => {
      if (value === "" || value === null || value === undefined) return 0;
      const n = Math.trunc(Number(value));
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };

    const shopee = toQty(allocation.shopee);
    const tokopedia = toQty(allocation.tokopedia);
    const spareInput = toQty(allocation.spare);
    const totalInput = shopee + tokopedia + spareInput;
    const autoSpare = Math.max(0, qtyValue - totalInput);

    return {
      shopee,
      tokopedia,
      spareInput,
      totalInput,
      effectiveSpare: spareInput + autoSpare,
      autoSpare,
      overflow: totalInput > qtyValue,
    };
  }, [allocation, qtyValue]);

  const productMatches = useMemo(() => {
    const q = String(productQuery || "").trim();
    if (!q) return [];
    return products.filter((p) => includesSearch(`${p?.kode_barang || ""} ${p?.nama_barang || ""}`, q)).slice(0, 12);
  }, [products, productQuery]);

  const stockInSummary = useMemo(() => {
    return {
      rows: stockIn.length,
      qty: stockIn.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0),
      pending: stockIn.filter((item) => String(item?.approval_status || "").toUpperCase() === "PENDING").length,
    };
  }, [stockIn]);

  const chooseProduct = (p) => {
    if (!p) return;
    setForm((f) => ({ ...f, kode_barang: String(p.kode_barang || "") }));
    setAllocation({ shopee: "", tokopedia: "", spare: "" });
    setProductQuery("");
    setProductOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "qty") {
        if (value === "") return { ...prev, [name]: "" };
        const num = Math.trunc(Number(value));
        if (Number.isFinite(num) && num > 0) return { ...prev, [name]: num };
        return { ...prev, [name]: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleAllocationChange = (field, value) => {
    setAllocation((prev) => {
      if (value === "") return { ...prev, [field]: "" };
      const num = Math.trunc(Number(value));
      if (Number.isFinite(num) && num >= 0) return { ...prev, [field]: num };
      return { ...prev, [field]: "" };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.kode_barang || form.qty <= 0) return;
    if (needsAllocation && allocationNumbers.overflow) {
      setError("Total alokasi tidak boleh melebihi qty masuk.");
      return;
    }

    const p = products.find((x) => x.kode_barang === form.kode_barang);
    const label = p ? `${p.kode_barang} - ${p.nama_barang}` : form.kode_barang;
    const allocationText = needsAllocation
      ? `\nAlokasi:\n- Shopee: ${allocationNumbers.shopee}\n- Tokopedia: ${allocationNumbers.tokopedia}\n- Spare/Manual: ${allocationNumbers.effectiveSpare}`
      : "\nAlokasi: Spare/Manual (item pendukung)";
    const ok = await dialog.confirm(`Simpan Stock Masuk?\n\nProduk: ${label}\nQty: ${form.qty}${allocationText}${form.keterangan ? `\nKeterangan: ${form.keterangan}` : ""}`, { title: "Konfirmasi", confirmText: "Simpan", cancelText: "Batal" });
    if (!ok) return;

    try {
      setLoading(true);
      await dialog.toastPromise(
        api.post("/stock-in", {
          tanggal: nowLocalSql(),
          ...form,
          allocation: needsAllocation ? allocation : undefined,
        }),
        {
          loading: "Menyimpan stock masuk…",
          success: () => `Tersimpan: ${label} x${form.qty}.`,
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menyimpan.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      setForm({ kode_barang: "", qty: 1, keterangan: "" });
      setAllocation({ shopee: "", tokopedia: "", spare: "" });
      setProductQuery("");
      setProductOpen(false);
      await fetchStockIn();
      setError("");
    } catch (e2) {
      setError(e2?.response?.data?.message || e2.message || "Gagal menyimpan");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!isAdmin) return;
    if (!item?.id) return;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const reasonRef = { current: "" };
      const ok = await dialog.custom(
        <DeleteReasonContent item={item} reasonRef={reasonRef} />,
        { title: "Hapus Transaksi", confirmText: "Hapus", cancelText: "Batal" }
      );
      if (!ok) return;

      const reason = String(reasonRef.current || "").trim();
      if (!reason) {
        await dialog.alert("Alasan penghapusan wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        setLoading(true);
        await dialog.toastPromise(
          api.delete(`/stock-in/${item.id}`, { data: { reason } }),
          {
            loading: "Menghapus…",
            success: "Transaksi terhapus.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menghapus.",
          },
          { successTitle: "Berhasil", errorTitle: "Gagal" }
        );
        await fetchStockIn();
        setError("");
      } catch (e2) {
        setError(e2?.response?.data?.message || e2.message || "Gagal menghapus");
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  const requestEdit = async (item) => {
    if (!item?.id) return;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const valueRef = { current: null };
      const ok = await dialog.custom(
        <RequestEditModal item={item} valueRef={valueRef} />,
        { title: "Request Edit", confirmText: "Kirim", cancelText: "Batal" }
      );
      if (!ok) return;

      const payload = valueRef.current || {};
      const reason = String(payload.reason || "").trim();
      if (!reason) {
        await dialog.alert("Alasan perubahan wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        setLoading(true);
        await dialog.toastPromise(
          api.post("/approval/request-edit", {
            module: "stock_in",
            target_id: item.id,
            new_data: { qty: payload.qty, keterangan: payload.keterangan },
            reason,
          }),
          {
            loading: "Mengirim request...",
            success: "Request edit terkirim.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal mengirim request.",
          },
          { successTitle: "Terkirim", errorTitle: "Gagal" }
        );
        await fetchStockIn();
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  const requestDelete = async (item) => {
    if (!item?.id) return;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const reasonRef = { current: "" };
      const ok = await dialog.custom(
        <RequestDeleteModal item={item} reasonRef={reasonRef} />,
        { title: "Request Hapus", confirmText: "Kirim", cancelText: "Batal" }
      );
      if (!ok) return;

      const reason = String(reasonRef.current || "").trim();
      if (!reason) {
        await dialog.alert("Alasan penghapusan wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        setLoading(true);
        await dialog.toastPromise(
          api.post("/approval/request-delete", {
            module: "stock_in",
            target_id: item.id,
            reason,
          }),
          {
            loading: "Mengirim request...",
            success: "Request hapus terkirim.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal mengirim request.",
          },
          { successTitle: "Terkirim", errorTitle: "Gagal" }
        );
        await fetchStockIn();
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Stock Masuk</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Catat barang masuk (restock) ke dalam sistem inventaris.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard icon={ClipboardList} title="Baris Transaksi" value={stockInSummary.rows} />
        <KpiCard icon={Boxes} title="Total Qty Masuk" value={stockInSummary.qty} />
        <KpiCard icon={PackagePlus} title="Menunggu Approval" value={stockInSummary.pending} />
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[13px] text-red-600">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[400px_minmax(0,1fr)] items-start">
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-zinc-900 mb-5">Input Stock Masuk</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Pilih Produk</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
                <input
                  value={productQuery}
                  onChange={(e) => {
                    setProductQuery(e.target.value);
                    setForm((f) => ({ ...f, kode_barang: "" }));
                    setAllocation({ shopee: "", tokopedia: "", spare: "" });
                    setProductOpen(true);
                  }}
                  onFocus={() => {
                    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                    setProductOpen(true);
                  }}
                  onBlur={() => {
                    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                    blurTimerRef.current = setTimeout(() => setProductOpen(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && productMatches[0]) {
                      e.preventDefault();
                      chooseProduct(productMatches[0]);
                    }
                    if (e.key === "Escape") setProductOpen(false);
                  }}
                  className="w-full rounded-md border border-zinc-300 pl-9 pr-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
                  placeholder="Ketik kode/nama produk"
                  autoComplete="off"
                />

                {productOpen && productMatches.length > 0 && (
                  <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg">
                    {productMatches.map((p) => (
                      <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chooseProduct(p)} className="flex w-full items-center gap-3 border-b border-zinc-100 px-3 py-2 text-left hover:bg-zinc-50 transition-colors">
                        <ProductIcon imageUrl={p.image_url} alt={p.nama_barang || "Produk"} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium text-zinc-900">{p.nama_barang || "(Tanpa nama)"}</div>
                          <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{p.kode_barang}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedProduct && (
                <div className="mt-2 flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50/50 p-2">
                  <ProductIcon imageUrl={selectedProduct.image_url} alt={selectedProduct.nama_barang || "Produk"} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-zinc-900">{selectedProduct.nama_barang || "(Tanpa nama)"}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-[11px] text-zinc-500">{selectedProduct.kode_barang}</span>
                      {selectedKategori && <span className="inline-flex rounded bg-zinc-200/50 px-1.5 py-0.5 text-[9px] font-semibold text-zinc-600 tracking-wide uppercase">{selectedKategori}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Qty (Jumlah)</label>
              <input 
                type="number" 
                name="qty" 
                value={form.qty} 
                onChange={handleChange} 
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
              />
            </div>

            {selectedProduct && needsAllocation && (
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3.5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Alokasi Channel</div>
                  <div className={`text-[11px] font-semibold ${allocationNumbers.overflow ? 'text-rose-600' : 'text-zinc-700'}`}>
                    Total: {allocationNumbers.totalInput} / {qtyValue}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] text-zinc-600 mb-1">Shopee</label>
                    <input type="number" min="0" value={allocation.shopee} onChange={(e) => handleAllocationChange("shopee", e.target.value)} className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-[13px] text-zinc-900 tabular-nums focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900" placeholder="0" />
                  </div>
                  <div>
                    <label className="block text-[12px] text-zinc-600 mb-1">Tokopedia</label>
                    <input type="number" min="0" value={allocation.tokopedia} onChange={(e) => handleAllocationChange("tokopedia", e.target.value)} className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-[13px] text-zinc-900 tabular-nums focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900" placeholder="0" />
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-zinc-600 mb-1">Spare / Manual / Gudang</label>
                  <input type="number" min="0" value={allocation.spare} onChange={(e) => handleAllocationChange("spare", e.target.value)} className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-[13px] text-zinc-900 tabular-nums focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900" placeholder="Otomatis sisanya" />
                </div>
                
                <div className={`text-[11px] ${allocationNumbers.overflow ? "text-rose-600" : "text-zinc-500"}`}>
                  {allocationNumbers.overflow
                    ? "Alokasi melebihi jumlah Qty!"
                    : `Sisa otomatis ke Spare: ${allocationNumbers.autoSpare}`}
                </div>
              </div>
            )}
            
            {selectedProduct && !needsAllocation && (
              <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600">
                Item pendukung otomatis masuk ke stok utama tanpa pembagian channel.
              </div>
            )}

            <div>
              <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Keterangan Tambahan</label>
              <textarea 
                name="keterangan" 
                value={form.keterangan} 
                onChange={handleChange} 
                placeholder="Opsional..."
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm min-h-[60px]"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading || (needsAllocation && allocationNumbers.overflow)} 
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none disabled:opacity-50"
            >
              {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" /> : <Save className="h-4 w-4" />}
              {loading ? "Menyimpan..." : "Simpan Stock Masuk"}
            </button>
          </form>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between bg-zinc-50/50">
            <h2 className="text-sm font-semibold text-zinc-900">Riwayat Stock Masuk Terbaru</h2>
            <span className="inline-flex rounded-full bg-zinc-200/50 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">{stockIn.length} record</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-[13px]">
              <thead className="border-b border-zinc-200 bg-white">
                <tr>
                  <th className="px-5 py-3 font-medium text-zinc-500">Tanggal & Approval</th>
                  <th className="px-5 py-3 font-medium text-zinc-500">Produk</th>
                  <th className="px-5 py-3 font-medium text-zinc-500 text-right">Qty</th>
                  <th className="px-5 py-3 font-medium text-zinc-500">Keterangan</th>
                  <th className="px-5 py-3 font-medium text-zinc-500 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading && stockIn.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500">Memuat data...</td></tr>
                ) : stockIn.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500">Belum ada riwayat stock masuk.</td></tr>
                ) : (
                  stockIn.map((item) => (
                    <tr key={item.id} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="px-5 py-3 align-top">
                        <div className="text-zinc-900 font-medium mb-1.5">{formatDateTime(item.tanggal) || "-"}</div>
                        <ApprovalBadge status={item.approval_status} />
                      </td>
                      <td className="px-5 py-3 align-top">
                        <div className="flex items-start gap-3">
                          <div className="hidden sm:block">
                            <ProductIcon imageUrl={item.image_url} alt={item.nama_barang || "Produk"} />
                          </div>
                          <div>
                            <div className="font-medium text-zinc-900">{item.nama_barang || item.kode_barang}</div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{item.kode_barang}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 align-top text-right font-semibold text-zinc-900 text-[14px]">
                        {item.qty}
                      </td>
                      <td className="px-5 py-3 align-top text-zinc-600 max-w-[200px] truncate" title={item.keterangan}>
                        {item.keterangan || "-"}
                      </td>
                      <td className="px-5 py-3 align-top text-right">
                        {isAdmin ? (
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => handleDelete(item)}
                            className="inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 px-2 py-1 text-[11px] font-medium text-rose-600 shadow-sm hover:bg-rose-50 hover:border-rose-200 transition-colors"
                          >
                            Hapus
                          </button>
                        ) : (
                          <div className="flex flex-col gap-1.5 items-end">
                            <button
                              type="button"
                              disabled={loading || item.approval_status === "PENDING"}
                              onClick={() => requestEdit(item)}
                              className="inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors disabled:opacity-50"
                            >
                              Req. Edit
                            </button>
                            <button
                              type="button"
                              disabled={loading || item.approval_status === "PENDING"}
                              onClick={() => requestDelete(item)}
                              className="inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 px-2 py-1 text-[11px] font-medium text-rose-600 shadow-sm hover:bg-rose-50 hover:border-rose-200 transition-colors disabled:opacity-50"
                            >
                              Req. Hapus
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockIn;
